import { useMemo, useState } from "react";
import { translationApi } from "@services/translationApi";
import { MAX_INPUT_CHARS, ALL_MODELS_VALUE } from "@models/translation";
import type { HistoryEntry, StartTranslationRequest } from "@models/translation";

interface Options {
  onSubmitted?: (entry: HistoryEntry) => void;
}

export const useTextTranslation = ({ onSubmitted }: Options = {}) => {
  const [sourceLang, setSourceLang] = useState("en");
  const [text, setText] = useState("");
  const [targets, setTargets] = useState<string[]>(["de"]);
  const [mode, setMode] = useState("forward");
  // "all" (the default) means: let the backend run all models and pick the best.
  const [model, setModel] = useState(ALL_MODELS_VALUE);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeTargets, setActiveTargets] = useState<string[]>([]);

  const overLimit = text.length > MAX_INPUT_CHARS;
  const canSubmit = useMemo(
    () => text.trim().length > 0 && targets.length > 0 && !overLimit && !submitting,
    [text, targets, overLimit, submitting],
  );

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const requestBody: StartTranslationRequest = {
        source_lang: sourceLang,
        target_lang: targets,
        text,
        llm_mode: mode,
      };
      // Only send llm_model when a specific model is chosen; omitting it makes
      // the backend run all models and return the best (the "all" default).
      if (model !== ALL_MODELS_VALUE) {
        requestBody.llm_model = [model];
      }
      const res = await translationApi.startTranslation(requestBody);
      const entry: HistoryEntry = {
        id: res.id,
        sourceLang,
        targetLangs: targets,
        mode,
        preview: text.slice(0, 140),
        createdAt: new Date().toISOString(),
      };
      onSubmitted?.(entry);
      setActiveTargets(targets);
      setActiveId(res.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start translation");
    } finally {
      setSubmitting(false);
    }
  };

  const openJob = (entry: HistoryEntry): void => {
    setSourceLang(entry.sourceLang);
    setTargets(entry.targetLangs);
    setMode(entry.mode);
    setActiveTargets(entry.targetLangs);
    setActiveId(entry.id);
  };

  // Clear the input and the displayed result back to the empty state, while
  // keeping the user's language/mode/model selections so they can translate
  // again with the same settings.
  const reset = (): void => {
    setText("");
    setActiveId(null);
    setActiveTargets([]);
    setError(null);
  };

  return {
    sourceLang,
    setSourceLang,
    text,
    setText,
    targets,
    setTargets,
    mode,
    setMode,
    model,
    setModel,
    submitting,
    error,
    overLimit,
    canSubmit,
    activeId,
    activeTargets,
    submit,
    openJob,
    reset,
  };
};
